import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, legacy_createStore as createStore } from 'redux';
import { composeWithDevTools } from '@redux-devtools/extension';
import { thunk } from 'redux-thunk';
import app from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCartContainer from './Containers/MiniCart.container';
import QuickSearchContainer from './Containers/QuickSearch.container';
import SunPanelsByRoof from './Containers/SunPanelsByRoof.container';
import NavigationContainer from './Containers/Navigation.container';
import FacetedSearchContainer from './Containers/FacetedSearch.container';
import FacetedSearchCompactContainer from './Containers/FacetedSearchCompact.container';
import StoreLocator from './Components/StoreLocator';
import MyPageEnjoyClubCollapse from './Components/MyPageEnjoyClubCollapse';
import SingleStoreMap from './Components/SingleStoreMap';
import AddressSearchBar from './Components/AddressSearchBar';
import DynamicComponent from './Components/DynamicComponent';
import ReorderOrderLineButtonContainer from './Containers/ReorderOrderLineButton.container';
import { initAccordion, initTabs, inIframe, initFAQAccordion } from './js';
import { configureStore } from '@reduxjs/toolkit';
import ListResellerOrder from './Components/ListResellerOrder';
import { initDataLayer } from './Containers/Tracking.container';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import ContactUsFormComponent from './Components/ContactUsForm.component';
import constants from './constants';
import CompanyForm from './Components/CompanyForm';
import SaunaForm from './Components/SaunaForm';
import ExpertBookForm from './Components/ExpertBookForm';
import FaqSection from './Components/FaqSection';
import { ProductEnergyIcon } from './Components/ProductEnergyIcon';
import CartCountContainer from './Containers/CartCount.container';
import { NuqsAdapter } from 'nuqs/adapters/react';

const registeredContainers = [];
const renderReact = (element, container, callback) => {
  registeredContainers.push(container);
  ReactDOM.render(element, container, callback);
};

window.onunload = () => {
  // make sure components are unmounted, redux's listener are unsubscribed
  // to fix issue with iframe navigation in IE
  registeredContainers.forEach((container) => {
    ReactDOM.unmountComponentAtNode(container);
  });
};

// Bind to window load in order to be sure that window.__litium is ready
window.addEventListener('load', () => {
  const isInIframe = window.self !== window.top;

  const bootstrapComponents = () => {
    if (inIframe()) {
      document.documentElement.classList.add('in-iframe');
    }

    initAccordion('#accordion');
    initFAQAccordion('#accordion');
    initTabs('#tabsSearch');
    initDataLayer();

    if (document.getElementById('event-tracking-container')) {
      const eventContainer = document.getElementById(
        'event-tracking-container'
      );
      if (eventContainer) {
        const event = JSON.parse(eventContainer.innerHTML);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          ecommerce: null,
        });
        window.dataLayer.push(event);
      }
    }

    if (document.getElementById('miniCart')) {
      renderReact(
        <Provider store={store}>
          <MiniCartContainer />
        </Provider>,
        document.getElementById('miniCart')
      );
    }

    const cartCountElement = document.querySelector('[data-id="cart-count"]');
    if (cartCountElement) {
      renderReact(
        <Provider store={store}>
          <CartCountContainer />
        </Provider>,
        cartCountElement
      );
    }

    if (document.getElementById('quickSearch')) {
      renderReact(
        <Provider store={store}>
          <QuickSearchContainer />
        </Provider>,
        document.getElementById('quickSearch')
      );
    }
    if (document.getElementById('addressSearchBar')) {
      const addressSearchBar = document.getElementById('addressSearchBar');
      const { sunpanelurl } = addressSearchBar.dataset;

      renderReact(
        <Provider store={store}>
          <AddressSearchBar url={sunpanelurl} />
        </Provider>,
        document.getElementById('addressSearchBar')
      );
    }
    if (document.getElementById('sunPanelsByRoof')) {
      const sunPanelsByRoof = document.getElementById('sunPanelsByRoof');
      const {
        title,
        mapAddressTitle,
        sunPanelPageUrl,
        variantSelectorTitle,
        amountSliderTitle,
        yourRoofTitle,
        yourRoofPreamble,
        yourRoofDefaultTypePreamble,
        yourRoofText,
        offerTitle,
        errorTitle,
        errorText,
        noOfferTitle,
        noOfferText,
        thanksTitle,
        thanksText,
        yourRoofModelTitle,
      } = sunPanelsByRoof.dataset;

      const googleReCaptchaSiteKey = constants.googleReCaptchaSiteKey;

      renderReact(
        <Provider store={store}>
          <GoogleReCaptchaProvider reCaptchaKey={googleReCaptchaSiteKey}>
            <SunPanelsByRoof
              title={title}
              mapAddressTitle={mapAddressTitle}
              sunPanelPageUrl={sunPanelPageUrl}
              variantSelectorTitle={variantSelectorTitle}
              amountSliderTitle={amountSliderTitle}
              yourRoofTitle={yourRoofTitle}
              yourRoofPreamble={yourRoofPreamble}
              yourRoofDefaultTypePreamble={yourRoofDefaultTypePreamble}
              yourRoofText={yourRoofText}
              offerTitle={offerTitle}
              errorTitle={errorTitle}
              errorText={errorText}
              noOfferTitle={noOfferTitle}
              noOfferText={noOfferText}
              thanksTitle={thanksTitle}
              thanksText={thanksText}
              yourRoofModelTitle={yourRoofModelTitle}
              apiKey={constants.solkartApiKey}
            />
          </GoogleReCaptchaProvider>
        </Provider>,
        document.getElementById('sunPanelsByRoof')
      );
    }

    const navigationElement = document.querySelector('[data-id="navigation"]');
    if (navigationElement) {
      renderReact(
        <Provider store={store}>
          <NavigationContainer />
        </Provider>,
        navigationElement
      );
    }
    if (document.getElementById('facetedSearch')) {
      renderReact(
        <Provider store={store}>
          <FacetedSearchContainer />
        </Provider>,
        document.getElementById('facetedSearch')
      );
    }
    const facetedSearchCompact = document.getElementById(
      'facetedSearchCompact'
    );
    if (facetedSearchCompact) {
      const { hidefilterdropdown } = facetedSearchCompact.dataset;
      const hideFilter =
        hidefilterdropdown && hidefilterdropdown.toLowerCase() === 'true';
      renderReact(
        <Provider store={store}>
          <FacetedSearchCompactContainer hideFilterDropdown={hideFilter} />
        </Provider>,
        document.getElementById('facetedSearchCompact')
      );
    }
    if (document.getElementById('myPagePersons')) {
      const PersonList = DynamicComponent({
        loader: () => import('./Containers/PersonList.container'),
      });
      renderReact(
        <Provider store={store}>
          <PersonList />
        </Provider>,
        document.getElementById('myPagePersons')
      );
    }
    if (document.getElementById('myPageAddresses')) {
      const AddressList = DynamicComponent({
        loader: () => import('./Containers/AddressList.container'),
      });
      renderReact(
        <Provider store={store}>
          <AddressList />
        </Provider>,
        document.getElementById('myPageAddresses')
      );
    }
    const checkoutElement = document.getElementById('checkout');
    if (checkoutElement) {
      const Checkout = DynamicComponent({
        loader: () => import('./Containers/Checkout.container'),
      });
      renderReact(
        <Provider store={store}>
          <Checkout />
        </Provider>,
        checkoutElement
      );
    }
    if (document.getElementById('lightBoxImages')) {
      const LightboxImages = DynamicComponent({
        loader: () => import('./Containers/LightboxImages.container'),
      });
      const rootElement = document.getElementById('lightBoxImages');
      const images = Array.from(rootElement.querySelectorAll('a')).map(
        (img) => ({
          src: img.dataset.src,
          alt: img.dataset.alt,
          srcset: img.dataset.srcset,
          sizes: img.dataset.sizes,
          loading: 'lazy'
        })
      );
      renderReact(
        <Provider store={store}>
          <LightboxImages images={images} />
        </Provider>,
        document.getElementById('lightBoxImages')
      );
    }

    if (document.querySelectorAll('.slider').length > 0) {
      const Slider = DynamicComponent({
        loader: () => import('./Components/Slider'),
      });
      Array.from(document.querySelectorAll('.slider')).forEach((slider) => {
        const values = [...slider.querySelectorAll('.slider__block')].map(
          (block) => {
            return {
              image: block.dataset.image,
              url: block.dataset.url,
              text: block.dataset.text,
              alt: block.dataset.alt,
            };
          }
        );
        if (values.length > 0) {
          renderReact(<Slider values={values} />, slider);
        }
      });
    }

    if (document.querySelectorAll('buy-button').length > 0) {
      const BuyButton = DynamicComponent({
        loader: () => import('./Containers/BuyButton.container'),
      });
      Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
        const {
          articleNumber,
          quantityFieldId,
          href,
          cssClass,
          label,
          isBuyAndCollectButton,
          variant,
        } = button.dataset;
        const isBuyAndCollect = isBuyAndCollectButton == 'true';
        renderReact(
          <Provider store={store}>
            <BuyButton
              {...{
                label,
                articleNumber,
                quantityFieldId,
                href,
                cssClass,
                isBuyAndCollect,
                variant,
              }}
            />
          </Provider>,
          button
        );
      });
    }
    if (document.querySelectorAll('my-dealer-button').length > 0) {
      const MyDealerButton = DynamicComponent({
        loader: () => import('./Containers/MyDealerButton.Container'),
      });
      Array.from(document.querySelectorAll('my-dealer-button')).forEach(
        (button) => {
          const {
            dealerId,
            isAuth,
            cssLoadingClass,
            isMyDealerLabel,
            cssClass,
            label,
            disableDealerBtn,
            disabledCssClass,
          } = button.dataset;
          renderReact(
            <Provider store={store}>
              <MyDealerButton
                {...{
                  label,
                  isAuth,
                  cssLoadingClass,
                  isMyDealerLabel,
                  dealerId,
                  cssClass,
                  disableDealerBtn,
                  disabledCssClass,
                }}
              />
            </Provider>,
            button
          );
        }
      );
    }

    if (document.querySelectorAll('reorder-button').length > 0) {
      const ReorderButton = DynamicComponent({
        loader: () => import('./Containers/ReorderButton.Container'),
      });
      Array.from(document.querySelectorAll('reorder-button')).forEach(
        (button) => {
          const { cssClass, orderId, title } = button.dataset;
          const label = button.innerText;
          renderReact(
            <Provider store={store}>
              <ReorderButton
                {...{
                  label,
                  title,
                  cssClass,
                  orderId,
                }}
              />
            </Provider>,
            button
          );
        }
      );
    }

    if (document.querySelectorAll('reorder-orderline-button').length > 0) {
      Array.from(document.querySelectorAll('reorder-orderline-button')).forEach(
        (button) => {
          const {
            cssClass,
            articleNumber,
            quantity,
            title,
            label,
            cssButtonClass,
          } = button.dataset;
          renderReact(
            <Provider store={store}>
              <ReorderOrderLineButtonContainer
                {...{
                  articleNumber,
                  quantity,
                  cssClass,
                  cssButtonClass,
                  label,
                  title,
                }}
              />
            </Provider>,
            button
          );
        }
      );
    }

    if (document.querySelectorAll('compare-button').length > 0) {
      const CompareButton = DynamicComponent({
        loader: () => import('./Containers/CompareButton.Container'),
      });
      Array.from(document.querySelectorAll('compare-button')).forEach(
        (button) => {
          const { cssClass, productId, title } = button.dataset;
          const label = button.innerText;
          renderReact(
            <Provider store={store}>
              <CompareButton
                {...{
                  label,
                  title,
                  cssClass,
                  productId,
                }}
              />
            </Provider>,
            button
          );
        }
      );
    }

    if (document.getElementById('my-page-bonus')) {
      const rootElement = document.getElementById('my-page-bonus');
      const {
        bonusAvailableLabel,
        bonusAvailable,
        bonusDescription,
        bonusImageUrl,
        bonusSignOut,
        bonusSignUp,
        bonusTitle,
        bonusNotMember,
      } = rootElement.dataset;
      renderReact(
        <MyPageEnjoyClubCollapse
          {...{
            bonusAvailableLabel,
            bonusAvailable,
            bonusTitle,
            bonusDescription,
            bonusImageUrl,
            bonusSignOut,
            bonusSignUp,
            bonusNotMember,
          }}
        />,
        document.getElementById('my-page-bonus')
      );
    }

    if (document.getElementById('compareProductBlock')) {
      const CompareProductBlock = DynamicComponent({
        loader: () => import('./Containers/CompareProductBlock.container'),
      });
      const rootElement = document.getElementById('compareProductBlock');
      const { linkToCompareProduct } = rootElement.dataset;
      renderReact(
        <Provider store={store}>
          <CompareProductBlock linkToCompareProduct={linkToCompareProduct} />
        </Provider>,
        document.getElementById('compareProductBlock')
      );
    }

    if (document.getElementById('storeLocator')) {
      const rootElement = document.getElementById('storeLocator');
      const jsonContainer = rootElement.querySelector('#storeLocatorJson');
      if (jsonContainer) {
        const data = jsonContainer && JSON.parse(jsonContainer.innerHTML);
        const stores = data.stores.map((storeObj, index) => ({
          ...storeObj,
          index: index,
        }));
        renderReact(
          <Provider store={store}>
            <StoreLocator stores={stores} />
          </Provider>,
          document.getElementById('storeLocator')
        );
      }
    }

    if (document.getElementById('myPageOrderList')) {
      const rootElement = document.getElementById('myPageOrderList');
      const jsonContainer = rootElement.querySelector('#myPageOrderListJson');
      if (jsonContainer) {
        const data = JSON.parse(jsonContainer.innerHTML);
        const orders = data && data.orders;
        const resellerOrderLink = data && data.resellerOrderLink;

        renderReact(
          <Provider store={store}>
            <ListResellerOrder
              orders={orders}
              resellerOrderLink={resellerOrderLink}
            />
          </Provider>,
          document.getElementById('myPageOrderList')
        );
      }
    }

    if (document.getElementById('singleStoreMap')) {
      const rootElement = document.getElementById('singleStoreMap');
      const jsonContainer = rootElement.querySelector('#singleStoreMapJson');
      if (jsonContainer) {
        const data = JSON.parse(jsonContainer.innerHTML);
        const storeObj = {
          ...data.store,
          index: 0,
        };
        renderReact(
          <Provider store={store}>
            <SingleStoreMap storeObj={storeObj} />
          </Provider>,
          document.getElementById('singleStoreMap')
        );
      }
    }

    if (document.getElementById('contact-us-form')) {
      const contactUsForm = document.getElementById('contact-us-form');
      const dataset = contactUsForm.dataset;
      let formData;
      let subjects;
      let labelList;
      if (dataset) {
        if (dataset.form) {
          formData = JSON.parse(dataset.form);
        }
        if (dataset.subjects) {
          subjects = JSON.parse(dataset.subjects);
        }
        if (dataset.labelList) {
          labelList = JSON.parse(dataset.labelList);
        }
      }

      const googleReCaptchaSiteKey = constants.googleReCaptchaSiteKey;
      renderReact(
        <GoogleReCaptchaProvider reCaptchaKey={googleReCaptchaSiteKey}>
          <ContactUsFormComponent
            formData={formData}
            subjects={subjects}
            labelList={labelList}
          ></ContactUsFormComponent>
        </GoogleReCaptchaProvider>,
        contactUsForm
      );
    }

    if (document.getElementById('company-form')) {
      const companyForm = document.getElementById('company-form');
      const dataset = companyForm.dataset;
      const googleReCaptchaSiteKey = constants.googleReCaptchaSiteKey;

      renderReact(
        <Provider store={store}>
          <GoogleReCaptchaProvider reCaptchaKey={googleReCaptchaSiteKey}>
            <CompanyForm descriptionHtml={dataset.descriptionHtml} />
          </GoogleReCaptchaProvider>
        </Provider>,
        companyForm
      );
    }

    if (document.getElementById('sauna-form')) {
      const saunaForm = document.getElementById('sauna-form');
      const dataset = JSON.parse(saunaForm.dataset.viewModel);
      const googleReCaptchaSiteKey = constants.googleReCaptchaSiteKey;

      renderReact(
        <Provider store={store}>
          <GoogleReCaptchaProvider reCaptchaKey={googleReCaptchaSiteKey}>
            <SaunaForm data={dataset} />
          </GoogleReCaptchaProvider>
        </Provider>,
        document.getElementById('sauna-form')
      );
    }

    if (document.getElementById('expert-booking-form')) {
      const saunaForm = document.getElementById('expert-booking-form');
      const dataset = JSON.parse(saunaForm.dataset.viewModel);
      const googleReCaptchaSiteKey = constants.googleReCaptchaSiteKey;

      renderReact(
        <Provider store={store}>
          <GoogleReCaptchaProvider reCaptchaKey={googleReCaptchaSiteKey}>
            <ExpertBookForm data={dataset} />
          </GoogleReCaptchaProvider>
        </Provider>,
        document.getElementById('expert-booking-form')
      );
    }

    if (document.querySelectorAll('[data-id="pdp-energy-icon"]')) {
      const elements = document.querySelectorAll('[data-id="pdp-energy-icon"]');

      Array.from(elements).forEach((element) => {
        const { energyImage, energyClass } = element.dataset;

        renderReact(
          <Provider store={store}>
          <ProductEnergyIcon
            imageUrl={energyImage}
            iconClassName={energyClass}
          />
        </Provider>,
        element
      );
    });
    }

    if (document.getElementById('faqSection') && !isInIframe) {
      const faqSection = document.getElementById('faqSection');
      const dataset = faqSection.dataset;

      renderReact(
        <Provider store={store}>
          <FaqSection data={dataset} />
        </Provider>,
        document.getElementById('faqSection')
      );
    }

    if (document.getElementById('orderHistoryPage')) {
      const OrderList = DynamicComponent({
        loader: () => import('./Components/MyPages/OrderHistoryListContainer'),
      });
      renderReact(
        <NuqsAdapter>
          <Provider store={store}>
            <OrderList />
          </Provider>
        </NuqsAdapter>,
        document.getElementById('orderHistoryPage')
      );
    }
  };

  const preloadState = window.__litium.preloadState || {};
  // use the parent page's store if possible, so the iframe will share the same store with the parent context.
  // this to makes for example the Reorder button in My Page page to update the state and reload the mini cart
  // when reorder action happens.
  const store = (window.__litium.store =
    window.__litium.store ||
    (window.parent.__litium && window.parent.__litium.store) ||
    configureStore({
      reducer: app,
      preloadedState: preloadState,
      devTools: composeWithDevTools(applyMiddleware(thunk, historyMiddleware)),
    }));

  window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
      // bootstrap React components, in case the HTML response we receive from the server
      // has React components. ReactDOM.render performs only an update on previous rendered
      // components and only mutate the DOM as necessary to reflect latest React element.
      bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
  };

  bootstrapComponents();
});
